import { useState } from "react";
import { useDispatch } from "react-redux";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";

import { ButtonProgress } from "components/utility/ButtonProgress";

import { i18n } from "services/i18nService";
import { canBeDeleted as canBinBeDeleted } from "services/sosInventoryService/bin/canBeDeleted";
import {
  deleteRecords,
  deleteRecord,
  DELETE_RECORDS_LOT_HAS_ONHAND,
  DELETE_RECORDS_LOT_USED_ON_TRANSACTIONS,
} from "services/sosInventoryService/sosApi";
import { handleProgramError } from "services/utility/errors";
import { checkForUnexpectedProps } from "services/utility/misc";

import { openAlert } from "globalState/alertSlice";
import { deleteNotifications } from "globalState/notificationsSlice";
import { recordsChangedTick } from "globalState/recordsChangedSlice";

import { getErrorCodeMessage } from "appErrorCodes";

const canBeDeleted = { bins: canBinBeDeleted };

export function DeleteDialog(props) {
  const {
    objectType,
    objectIds,
    objectId,
    message,
    open,
    onClose,
    deleteSingle,
    records,
    ...unexpected
  } = props;
  checkForUnexpectedProps("DeleteDialog", unexpected);

  const [inProgress, setInProgress] = useState(false);

  const dispatch = useDispatch();

  function doDelete() {
    deleteSingle ? singleDelete() : batchDelete();
  }

  async function singleDelete() {
    const type = i18n(`objectType.${objectType}.Lower`);
    setInProgress(true);
    const { success, message, errorCode, details } = await deleteRecord(
      objectType,
      objectId
    );
    if (success) {
      const successMessage = i18n("alert.SuccessDelete", { type });
      dispatch(openAlert({ type: "success", message: successMessage }));
      dispatch(recordsChangedTick());
      onClose();
    } else if (errorCode) {
      const errorMessage = getErrorCodeMessage(errorCode, details);
      dispatch(
        openAlert({
          type: "error",
          message: errorMessage || message || i18n("global.Error"),
        })
      );
    } else {
      const errorMessage = message || i18n("alert.ErrorDelete", { type });
      dispatch(openAlert({ type: "error", message: errorMessage }));
    }
    setInProgress(false);
  }

  async function batchDelete() {
    if (canBeDeleted.hasOwnProperty(objectType)) {
      const errors = canBeDeleted[objectType](records, objectIds);
      if (errors.messages.length) {
        const message = errors.messages.join(" ");
        dispatch(openAlert({ type: "error", message }));
        onClose();
        return;
      }
    }
    setInProgress(true);
    const { success, message, errorCode, details } = await deleteRecords(
      objectType,
      objectIds
    );
    setInProgress(false);
    if (success) {
      onClose();
      dispatch(
        openAlert({ type: "success", message: i18n("alert.SuccessDeleted") })
      );
      // Updates top nav list to reflect deleted notifications
      if (objectType === "notification") {
        dispatch(deleteNotifications(objectIds));
      }
    } else if (errorCode) {
      const errorMessage = getErrorCodeMessage(errorCode, details);
      dispatch(
        openAlert({
          type: "error",
          message: errorMessage || message || i18n("global.Error"),
        })
      );
    } else {
      switch (message) {
        case DELETE_RECORDS_LOT_HAS_ONHAND:
          dispatch(
            openAlert({
              type: "error",
              message: i18n("lots.DeleteRecordsLotHasOnhand"),
            })
          );
          break;
        case DELETE_RECORDS_LOT_USED_ON_TRANSACTIONS:
          dispatch(
            openAlert({
              type: "error",
              message: i18n("lots.DeleteRecordsLotUsedOnTransactions"),
            })
          );
          break;
        default:
          handleProgramError(new Error(message || i18n("global.Error")));
      }
    }
    dispatch(recordsChangedTick());
    onClose();
  }

  return (
    <Dialog open={open} fullWidth maxWidth="xs" sx={{ inset: "0 0 40%" }}>
      <DialogTitle>{i18n("global.Delete")}</DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          {i18n("button.Cancel")}
        </Button>
        <div style={{ position: "relative" }}>
          <Button color="secondary" disabled={inProgress} onClick={doDelete}>
            {i18n("button.Delete")}
          </Button>
          {inProgress && <ButtonProgress color="secondary" />}
        </div>
      </DialogActions>
    </Dialog>
  );
}
